import { useState, useEffect } from 'react';
import { Button, Input } from 'components';
import {
  previewCancellation,
  RefundCalculation,
  SubscriptionPreview,
} from 'shared/services/subscription-cancellation.service';
import './Modal-custom-refund.scss';

interface ModalCustomRefundProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (amount: number, isAutomatic: boolean) => void;
  isLoading?: boolean;
  maxAmount?: number;
  // Parámetros para obtener el cálculo del servidor
  subscriptionId: string;
  uid: string;
  userImpersonationId: string;
}

export const ModalCustomRefund = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  maxAmount,
  subscriptionId,
  uid,
  userImpersonationId,
}: ModalCustomRefundProps) => {
  const [isAutomaticMode, setIsAutomaticMode] = useState<boolean>(true);
  const [amount, setAmount] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Estados para el cálculo del servidor
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string>('');
  const [refundCalculation, setRefundCalculation] =
    useState<RefundCalculation | null>(null);
  const [subscriptionPreview, setSubscriptionPreview] =
    useState<SubscriptionPreview | null>(null);

  // Obtener el cálculo del reembolso del servidor cuando se abre el modal
  useEffect(() => {
    const fetchPreview = async () => {
      if (!isOpen || !subscriptionId || !uid || !userImpersonationId) {
        return;
      }

      setIsLoadingPreview(true);
      setPreviewError('');
      setRefundCalculation(null);
      setSubscriptionPreview(null);

      try {
        const response = await previewCancellation({
          subscriptionId,
          uid,
          userImpersonationId,
        });

        if (response.success && response.refund_calculation) {
          setRefundCalculation(response.refund_calculation);
          setSubscriptionPreview(response.subscription || null);
        } else {
          setPreviewError(
            response.message || 'Error al obtener la información del reembolso'
          );
        }
      } catch (err) {
        console.error('Error fetching preview:', err);
        setPreviewError('Error al obtener la información del reembolso');
      } finally {
        setIsLoadingPreview(false);
      }
    };

    fetchPreview();
  }, [isOpen, subscriptionId, uid, userImpersonationId]);

  // Monto de reembolso automático calculado por el servidor
  const automaticRefundAmount = refundCalculation?.refund_amount || 0;

  // Calcular días restantes basado en la información del servidor
  const daysRemaining = (() => {
    if (!subscriptionPreview?.date_expire) {
      return 0;
    }

    try {
      const expireDate = new Date(subscriptionPreview.date_expire);
      expireDate.setHours(0, 0, 0, 0);
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      const timeDiff = expireDate.getTime() - currentDate.getTime();
      const days = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

      return days > 0 ? days : 0;
    } catch {
      return 0;
    }
  })();

  const handleAmountChange = (value: string) => {
    setAmount(value);
    setError('');
  };

  const handleConfirm = () => {
    if (isAutomaticMode) {
      // Usar el monto calculado automáticamente
      if (automaticRefundAmount <= 0) {
        setError('No hay monto disponible para reembolsar');
        return;
      }
      onConfirm(automaticRefundAmount, true);
      return;
    }

    // Validar monto personalizado
    const numericAmount = parseFloat(amount);

    if (!amount || isNaN(numericAmount)) {
      setError('Por favor ingresa un monto válido');
      return;
    }

    if (numericAmount <= 0) {
      setError('El monto debe ser mayor a cero');
      return;
    }

    if (maxAmount && numericAmount > maxAmount) {
      setError(`El monto no puede ser mayor a $${maxAmount}`);
      return;
    }

    onConfirm(numericAmount, false);
  };

  const handleClose = () => {
    setAmount('');
    setError('');
    setIsAutomaticMode(true);
    setPreviewError('');
    setRefundCalculation(null);
    setSubscriptionPreview(null);
    onClose();
  };

  const handleModeChange = (automatic: boolean) => {
    setIsAutomaticMode(automatic);
    setError('');
    if (automatic) {
      setAmount('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-custom-refund__overlay">
      <div className="modal-custom-refund__container">
        <div className="modal-custom-refund__content">
          <h3 className="modal-custom-refund__title">
            Reembolso Personalizado
          </h3>
          <p className="modal-custom-refund__message">
            {isAutomaticMode
              ? 'El monto se calculará automáticamente basado en el período restante'
              : 'Ingresa el monto que deseas reembolsar al usuario'}
          </p>

          {/* Switch para elegir modo */}
          <div className="modal-custom-refund__switch-container">
            <button
              type="button"
              className={`modal-custom-refund__switch-option ${
                isAutomaticMode ? 'active' : ''
              }`}
              onClick={() => handleModeChange(true)}
            >
              Reembolso Automático
            </button>
            <button
              type="button"
              className={`modal-custom-refund__switch-option ${
                !isAutomaticMode ? 'active' : ''
              }`}
              onClick={() => handleModeChange(false)}
            >
              Monto Personalizado
            </button>
          </div>

          <div className="modal-custom-refund__input-container">
            <label className="modal-custom-refund__label">
              Monto del reembolso
            </label>
            {isAutomaticMode ? (
              <div className="modal-custom-refund__automatic-amount">
                {isLoadingPreview ? (
                  <div className="modal-custom-refund__loading">
                    <p>Calculando reembolso...</p>
                  </div>
                ) : previewError ? (
                  <div className="modal-custom-refund__preview-error">
                    <p>{previewError}</p>
                  </div>
                ) : (
                  <>
                    <Input
                      type="decimal"
                      value={automaticRefundAmount.toFixed(2)}
                      onChange={() => {
                        // No action needed - this is read-only
                      }}
                      placeholder="0.00"
                      errorInput=""
                      prefix="$"
                      maxLength={10}
                      isRequired={false}
                      isTouched={false}
                      name="automatic-amount"
                      id="automatic-amount"
                      isDisabled={true}
                    />
                    <p className="modal-custom-refund__automatic-info">
                      {daysRemaining > 0
                        ? `Monto calculado automáticamente basado en el período restante de ${daysRemaining} día${
                            daysRemaining !== 1 ? 's' : ''
                          }`
                        : 'No hay días restantes'}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <>
                <Input
                  type="decimal"
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder="0.00"
                  errorInput={error}
                  prefix="$"
                  maxLength={10}
                  isRequired={true}
                  isTouched={true}
                  name="amount"
                  id="amount"
                />
                {error && (
                  <p className="modal-custom-refund__error-message">{error}</p>
                )}
                {maxAmount && !error && (
                  <p className="modal-custom-refund__max-amount">
                    Monto máximo: ${maxAmount}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="modal-custom-refund__actions">
            <Button
              template="secondary"
              typeStyle="square"
              color="primary"
              size="small"
              onClick={handleClose}
              isDisabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              template="primary"
              typeStyle="square"
              color="primary"
              size="small"
              onClick={handleConfirm}
              isLoading={isLoading || isLoadingPreview}
              isDisabled={
                isAutomaticMode
                  ? automaticRefundAmount <= 0 ||
                    !!error ||
                    isLoadingPreview ||
                    !!previewError
                  : !amount || !!error
              }
            >
              Continuar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
